<template>
  <div class="container-fluid">
    <div class="d-sm-flex align-items-center justify-content-between mb-4">
      <h1 class="h3 mb-0 text-gray-800">DDI Management</h1>
    </div>

    <div class="row">
      <div class="col-sm-6 col-xl-3">
        <div class="p-3 card border-left-primary mb-g">
          <div>
            <small class="text-muted fs-sm">Total DDIs</small>
            <h3 class="d-block l-h-n m-0 fw-700 text-primary">{{ totalDDIs }}</h3>
          </div>
        </div>
      </div>
      <div class="col-sm-6 col-xl-3">
        <div class="p-3 card border-left-success mb-g">
          <div>
            <small class="text-muted fs-sm">Active</small>
            <h3 class="d-block l-h-n m-0 fw-700 text-success">{{ activeDDIs }}</h3>
          </div>
        </div>
      </div>
      <div class="col-sm-6 col-xl-3">
        <div class="p-3 card border-left-secondary mb-g">
          <div>
            <small class="text-muted fs-sm">Countries</small>
            <h3 class="d-block l-h-n m-0 fw-700 text-secondary">{{ countriesCount }}</h3>
          </div>
        </div>
      </div>
      <div class="col-sm-6 col-xl-3">
        <div class="p-3 card border-left-warning mb-g">
          <div>
            <small class="text-muted fs-sm">Suppliers</small>
            <h3 class="d-block l-h-n m-0 fw-700 text-warning">{{ suppliersCount }}</h3>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-12 mt-3">
        <div class="card mb-4">
          <div class="card-header py-3">
            <h6 class="m-0 font-weight-bold text-primary">
              <svg xmlns="http://www.w3.org/2000/svg" style="vertical-align: sub;" width="24" height="24"
                viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                stroke-linecap="round" stroke-linejoin="round"
                class="lucide lucide-filter w-5 h-5 text-primary">
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
              </svg>
              Search &amp; Filter
            </h6>
          </div>
          <div class="card-body">
            <form @submit.prevent>
              <div class="row">
                <div class="col-md-6">
                  <div class="input-group bg-white shadow-inset-2">
                    <div class="input-group-prepend">
                      <span class="input-group-text bg-transparent border-right-0">
                        <font-awesome-icon :icon="['fas', 'search']" />
                      </span>
                    </div>
                    <input v-model.trim="searchQuery" type="text" class="form-control border-left-0 bg-transparent pl-0"
                      placeholder="Search by ID, supplier or DDI number...">
                  </div>
                </div>

                <div class="col-md-2">
                  <select class="custom-select w-100" v-model="countryFilter">
                    <option value="">All Countries</option>
                    <option v-for="country in countryOptions" :key="`country-${country}`" :value="country">{{ country }}</option>
                  </select>
                </div>
                <div class="col-md-2">
                  <select class="custom-select w-100" v-model="supplierFilter">
                    <option value="">All Suppliers</option>
                    <option v-for="supplier in supplierOptions" :key="`supplier-${supplier}`" :value="supplier">{{ supplier }}</option>
                  </select>
                </div>
                <div class="col-md-2">
                  <select class="custom-select w-100" v-model="statusFilter">
                    <option value="">All Statuses</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-md-12 d-flex justify-content-between align-items-center">
        <div class="d-flex align-items-center">
          <div class="form-inline mb-3">
            <div class="form-group">
              <label class="pr-2">Results per page: </label>
              <select class="custom-select" v-model.number="perPage">
                <option :value="5">5</option>
                <option :value="10">10</option>
                <option :value="15">15</option>
                <option :value="0">All</option>
              </select>
            </div>
          </div>
          <p class="text-primary ml-3">Showing <strong>{{ showingCount }}</strong> of <strong>{{ filteredCount }}</strong> DDIs</p>
        </div>

        <div>
          <button class="btn btn-outline-primary mr-3" type="button" disabled title="Provisioning not available yet"><font-awesome-icon :icon="['fas', 'phone-alt']" />
            Provision DDI</button>
          <button class="btn btn-primary" type="button" @click="showModal('add')"><font-awesome-icon
              :icon="['fas', 'plus']" /> New DDI</button>
        </div>
      </div>

      <div class="col-md-12" v-if="errorMessage">
        <div class="alert alert-danger">{{ errorMessage }}</div>
      </div>

      <div class="col-md-12 table-responsive">
        <table class="table table-bordered table-striped table-hover">
          <thead>
            <tr>
              <th scope="col">ID</th>
              <th scope="col">DDI</th>
              <th scope="col">Country</th>
              <th scope="col">Supplier</th>
              <th scope="col">Status</th>
              <th scope="col" style="width: 250px;">Created</th>
              <th class="text-right" scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="isLoading">
              <td colspan="7" class="text-center text-muted py-4">Loading DDIs...</td>
            </tr>
            <tr v-else-if="!pagedDDIs.length">
              <td colspan="7" class="text-center text-muted py-4">No DDIs found.</td>
            </tr>
            <tr v-else v-for="ddi in pagedDDIs" :key="ddi.id">
              <td><a href="#" class="font-weight-bold" @click.prevent="openDDIDetails(ddi)">{{ formatDDIIdentifier(ddi.id) }}</a></td>
              <td>{{ ddi.number }}</td>
              <td><span class="badge border border-secondary text-dark badge-pill">{{ ddi.country }}</span></td>
              <td>{{ ddi.supplier }}</td>
              <td><span :class="statusBadgeClass(ddi.status)">{{ statusLabel(ddi.status) }}</span></td>
              <td>{{ ddi.createdAt }}</td>
              <td class="text-right">
                <a href="#" :class="['btn', 'btn-icon', 'btn-sm', 'btn-action', ddiActionClass(ddi)]"
                  @click.prevent="openDDIStatusModal(ddi)">
                  <font-awesome-icon :icon="ddiActionIcon(ddi)" />
                </a>
              </td>
            </tr>
          </tbody>
        </table>

        <nav aria-label="pagination" v-if="hasPagination">
          <ul class="pagination justify-content-end">
            <li class="page-item" :class="{ disabled: currentPage === 1 }">
              <a class="page-link" href="#" @click.prevent="goToPage(currentPage - 1)">Previous</a>
            </li>
            <li class="page-item" v-for="page in paginationPages" :key="`ddis-page-${page}`" :class="{ active: currentPage === page }">
              <a class="page-link" href="#" @click.prevent="goToPage(page)">{{ page }}</a>
            </li>
            <li class="page-item" :class="{ disabled: currentPage === totalPages }">
              <a class="page-link" href="#" @click.prevent="goToPage(currentPage + 1)">Next</a>
            </li>
          </ul>
        </nav>
      </div>
    </div>

    <div :class="['modal', 'fade', 'show']" :style="{ display: showModalDetails ? 'block' : 'none' }" id="modalDetailsDDI" tabindex="-1"
      role="dialog" aria-labelledby="modalDetailsDDI" aria-hidden="true" data-backdrop="static"
      data-keyboard="false" v-if="showModalDetails">
      <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content" v-if="selectedDDI">
          <div class="modal-header d-block">
            <p class="h5 modal-title" id="exampleModalLongTitle">DDI Details</p>
            <div class="d-flex align-items-center justify-content-between pt-3">
              <small>{{ formatDDIIdentifier(selectedDDI.id) }}</small>
              <span :class="statusBadgeClass(selectedDDI.status)">{{ statusLabel(selectedDDI.status) }}</span>
            </div>
            <button type="button" class="close" data-dismiss="modal" aria-label="Close" @click="hideModal('details')">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <ul class="list-group list-group-flush">
              <li class="list-group-item"><label :style="{ width: '120px' }">DDI Number
                </label><strong>{{ selectedDDI.number }}</strong></li>
              <li class="list-group-item"><label :style="{ width: '120px' }">Country
                </label><strong>{{ selectedDDI.country }}</strong></li>
              <li class="list-group-item"><label :style="{ width: '120px' }">Supplier
                </label><strong>{{ selectedDDI.supplier }}</strong></li>
              <li class="list-group-item"><label :style="{ width: '120px' }">Status
                </label><strong>{{ statusLabel(selectedDDI.status) }}</strong></li>
              <li class="list-group-item"><label :style="{ width: '120px' }">Created:
                </label><strong>{{ selectedDDI.createdAt }}</strong></li>
            </ul>

            <div class="modal-footer">
              <button
                type="button"
                :class="['btn', selectedDDI.status === 'inactive' ? 'btn-success' : 'btn-danger']"
                @click="openDDIStatusModal(selectedDDI)"
              >
                {{ selectedDDI.status === 'inactive' ? 'Enable DDI' : 'Disable DDI' }}
              </button>
              <button type="button" class="btn btn-secondary" data-dismiss="modal" @click="hideModal('details')">Close</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div :class="['modal', 'fade', 'show']" :style="{ display: showModalAdd ? 'block' : 'none' }" id="modalAddDDI" tabindex="-1"
      role="dialog" aria-labelledby="modalAddDDI" aria-hidden="true" data-backdrop="static" data-keyboard="false" v-if="showModalAdd">
      <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content">
          <div class="modal-header d-block">
            <p class="h5 modal-title" id="exampleModalLongTitle">Create New DDI</p>
            <button type="button" class="close" data-dismiss="modal" aria-label="Close" @click="hideModal('add')">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <div v-if="addDDIError" class="alert alert-danger">{{ addDDIError }}</div>
            <ul class="list-group list-group-flush">
              <li class="list-group-item d-flex align-items-center"><label :style="{ width: '130px' }">DDI
                  Number (E.164)*
                </label>
                <input v-model.trim="ddiForm.number" type="text" class="form-control" placeholder="+442033221474" required>
              </li>
              <li class="list-group-item d-flex align-items-center"><label
                  :style="{ width: '130px' }">Supplier*
                </label>
                <input v-model.trim="ddiForm.supplier" type="text" class="form-control" placeholder="Twilio" required>
              </li>
              <li class="list-group-item d-flex align-items-center"><label
                  :style="{ width: '130px' }">Status*
                </label>
                <select class="custom-select w-100" v-model="ddiForm.status" required>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </li>
            </ul>

            <div class="modal-footer">
              <button type="button" class="btn btn-primary" :disabled="isSubmittingDDI" @click="submitNewDDI">{{ isSubmittingDDI ? 'Saving...' : 'Create DDI' }}</button>
              <button type="button" class="btn btn-secondary" data-dismiss="modal" @click="hideModal('add')">Cancel</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div :class="['modal', 'fade', 'show']" :style="{ display: showModalProv ? 'block' : 'none' }" id="modalProvDDI" tabindex="-1"
      role="dialog" aria-labelledby="modalProvDDI" aria-hidden="true" data-backdrop="static"
      data-keyboard="false" v-if="showModalProv">
      <div class="modal-dialog modal-dialog-centered modal-lg" role="document">
        <div class="modal-content">
          <div class="modal-header d-block">
            <p class="h5 modal-title" id="exampleModalLongTitle">Provision New DDI</p>
            <small>Search for available phone numbers via Twilio and provision them.</small>
            <button type="button" class="close" data-dismiss="modal" aria-label="Close" @click="hideModal('prov')">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <div class="alert alert-info mb-0">
              Provisioning workflow is not enabled in backend yet.
            </div>
            <div class="d-flex justify-content-end mt-3">
              <button type="button" class="btn btn-secondary" data-dismiss="modal" @click="hideModal('prov')">Close</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div :class="['modal', 'fade', 'show']" :style="{ display: showModalConfirm ? 'block' : 'none' }" id="modalDDIStatus" tabindex="-1"
      role="dialog" aria-labelledby="modalDDIStatus" aria-hidden="true" data-backdrop="static"
      data-keyboard="false" v-if="showModalConfirm">
      <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content">
          <div class="modal-header d-block">
            <p class="h5 modal-title" id="exampleModalLongTitle">{{ pendingDDIAction === 'enable' ? 'Enable DDI' : 'Disable DDI' }}</p>
            <button type="button" class="close" data-dismiss="modal" aria-label="Close" @click="hideModal('confirm')">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <p v-if="pendingDDIAction === 'disable'">Are you sure you want to disable <strong>{{ pendingStatusDDI ? pendingStatusDDI.number : '' }}</strong>?</p>
            <p v-else>Are you sure you want to enable <strong>{{ pendingStatusDDI ? pendingStatusDDI.number : '' }}</strong>?</p>

            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" data-dismiss="modal" @click="hideModal('confirm')">Cancel</button>
              <button type="button" :class="['btn', pendingDDIAction === 'enable' ? 'btn-success' : 'btn-danger']" :disabled="isUpdatingDDIStatus" @click="confirmDDIStatusAction">{{ isUpdatingDDIStatus ? (pendingDDIAction === 'enable' ? 'Enabling...' : 'Disabling...') : (pendingDDIAction === 'enable' ? 'Enable' : 'Disable') }}</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ApiService from '@/services/api.js';

const COUNTRY_PREFIXES = [
  { prefix: '+53', label: 'CU' },
  { prefix: '+44', label: 'UK' },
  { prefix: '+1', label: 'US' },
  { prefix: '+34', label: 'ES' },
  { prefix: '+33', label: 'FR' },
  { prefix: '+49', label: 'DE' },
  { prefix: '+39', label: 'IT' },
  { prefix: '+351', label: 'PT' },
  { prefix: '+55', label: 'BR' },
  { prefix: '+61', label: 'AU' },
  { prefix: '+81', label: 'JP' },
];

const emptyDDIForm = () => ({
  number: '',
  supplier: '',
  status: 'active',
});

const DDI_STATUS_META = {
  active: { label: 'Active', badge: 'badge border border-success text-success badge-pill' },
  inactive: { label: 'Inactive', badge: 'badge border border-secondary text-secondary badge-pill' },
};

export default {
  name: 'DDIs',
  data: () => ({
    ddis: [],
    isLoading: false,
    isSubmittingDDI: false,
    errorMessage: null,
    addDDIError: null,
    searchQuery: '',
    countryFilter: '',
    supplierFilter: '',
    statusFilter: '',
    perPage: 5,
    currentPage: 1,
    showModalDetails: false,
    showModalAdd: false,
    showModalProv: false,
    showModalConfirm: false,
    selectedDDIId: null,
    pendingStatusDDI: null,
    pendingDDIAction: 'disable',
    isUpdatingDDIStatus: false,
    ddiForm: emptyDDIForm(),
  }),
  computed: {
    normalizedDDIs() {
      return (this.ddis || [])
        .map((ddi) => {
          const number = (ddi && ddi.name) || '';
          const createdAtTs = Number(ddi && ddi.created_at) || 0;
          return {
            id: ddi.id,
            number,
            country: (ddi && ddi.country) || this.countryFromNumber(number),
            supplier: (ddi && ddi.description) || '—',
            status: ((ddi && ddi.status) || 'active').toLowerCase(),
            createdAtTs,
            createdAt: this.formatDate(ddi && ddi.created_at),
          };
        })
        .sort((a, b) => {
          const inactiveDiff = Number(a.status === 'inactive') - Number(b.status === 'inactive');
          if (inactiveDiff !== 0) return inactiveDiff;
          const createdDiff = (b.createdAtTs || 0) - (a.createdAtTs || 0);
          if (createdDiff !== 0) return createdDiff;
          return b.id - a.id;
        });
    },
    totalDDIs() {
      return this.normalizedDDIs.length;
    },
    activeDDIs() {
      return this.normalizedDDIs.filter((ddi) => ddi.status === 'active').length;
    },
    countriesCount() {
      return this.countryOptions.length;
    },
    suppliersCount() {
      return this.supplierOptions.length;
    },
    countryOptions() {
      return Array.from(new Set(this.normalizedDDIs.map((ddi) => ddi.country))).filter(Boolean).sort();
    },
    supplierOptions() {
      return Array.from(new Set(this.normalizedDDIs.map((ddi) => ddi.supplier))).filter(Boolean).sort();
    },
    filteredDDIs() {
      const query = (this.searchQuery || '').toLowerCase();
      return this.normalizedDDIs.filter((ddi) => {
        if (this.statusFilter && ddi.status !== this.statusFilter) return false;
        if (this.countryFilter && ddi.country !== this.countryFilter) return false;
        if (this.supplierFilter && ddi.supplier !== this.supplierFilter) return false;
        if (!query) return true;
        return [
          this.formatDDIIdentifier(ddi.id),
          ddi.number,
          ddi.country,
          ddi.supplier,
        ]
          .filter(Boolean)
          .map((value) => String(value).toLowerCase())
          .some((value) => value.includes(query));
      });
    },
    filteredCount() {
      return this.filteredDDIs.length;
    },
    totalPages() {
      if (this.perPage === 0) return 1;
      return Math.max(1, Math.ceil(this.filteredCount / this.perPage));
    },
    paginationPages() {
      return Array.from({ length: this.totalPages }, (_, index) => index + 1);
    },
    hasPagination() {
      return this.totalPages > 1;
    },
    pagedDDIs() {
      if (this.perPage === 0) return this.filteredDDIs;
      const start = (this.currentPage - 1) * this.perPage;
      return this.filteredDDIs.slice(start, start + this.perPage);
    },
    showingCount() {
      return this.pagedDDIs.length;
    },
    selectedDDI() {
      return this.normalizedDDIs.find((ddi) => ddi.id === this.selectedDDIId) || null;
    },
  },
  watch: {
    searchQuery() {
      this.currentPage = 1;
    },
    countryFilter() {
      this.currentPage = 1;
    },
    supplierFilter() {
      this.currentPage = 1;
    },
    statusFilter() {
      this.currentPage = 1;
    },
    perPage(value) {
      if (value < 0) this.perPage = 0;
      this.currentPage = 1;
    },
    filteredCount() {
      if (this.currentPage > this.totalPages) {
        this.currentPage = this.totalPages;
      }
    },
  },
  created() {
    this.fetchDDIs();
  },
  methods: {
    fetchDDIs() {
      this.isLoading = true;
      this.errorMessage = null;
      return ApiService.getDDIs()
        .then((response) => {
          this.ddis = response.data || [];
        })
        .catch((error) => {
          this.errorMessage = this.extractApiError(error, 'Unable to load DDIs.');
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    showModal(modal) {
      this.showModalDetails = modal === 'details';
      this.showModalAdd = modal === 'add';
      this.showModalProv = modal === 'prov';
      this.showModalConfirm = modal === 'confirm';
      if (modal === 'add') {
        this.ddiForm = emptyDDIForm();
        this.addDDIError = null;
      }
    },
    hideModal(modal) {
      if (modal === 'details') {
        this.showModalDetails = false;
        this.selectedDDIId = null;
      } else if (modal === 'add') {
        this.showModalAdd = false;
        this.addDDIError = null;
      } else if (modal === 'confirm') {
        this.showModalConfirm = false;
        this.pendingStatusDDI = null;
        this.pendingDDIAction = 'disable';
      } else {
        this.showModalProv = false;
      }
    },
    formatDDIIdentifier(id) {
      if (id == null) return '';
      return `DDI-${String(id).padStart(3, '0')}`;
    },
    countryFromNumber(number) {
      const normalized = String(number || '').trim();
      const match = COUNTRY_PREFIXES.find((entry) => normalized.startsWith(entry.prefix));
      return match ? match.label : 'Unknown';
    },
    formatDate(timestamp) {
      if (!timestamp) return '—';
      const numeric = Number(timestamp);
      const ms = Number.isNaN(numeric) ? Date.parse(timestamp) : numeric * 1000;
      if (Number.isNaN(ms)) return '—';
      const date = new Date(ms);
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const day = String(date.getDate()).padStart(2, '0');
      const month = months[date.getMonth()] || '---';
      const year = String(date.getFullYear()).slice(-2);
      return `${day}-${month}-${year}`;
    },
    statusMeta(status) {
      return DDI_STATUS_META[status] || DDI_STATUS_META.active;
    },
    statusLabel(status) {
      return this.statusMeta(status).label;
    },
    statusBadgeClass(status) {
      return this.statusMeta(status).badge;
    },
    ddiActionClass(ddi) {
      return ddi && ddi.status === 'inactive' ? 'btn-outline-success' : 'btn-outline-danger';
    },
    ddiActionIcon(ddi) {
      return ddi && ddi.status === 'inactive' ? ['fas', 'check'] : ['fas', 'ban'];
    },
    goToPage(page) {
      if (page < 1 || page > this.totalPages || page === this.currentPage) return;
      this.currentPage = page;
    },
    isValidDDINumber(number) {
      return /^\+[1-9]\d{6,14}$/.test((number || '').trim());
    },
    extractApiError(error, fallbackMessage) {
      const data = error && error.response ? error.response.data : null;
      if (data && typeof data === 'object' && data.error) return String(data.error);
      if (typeof data === 'string') return data;
      return fallbackMessage;
    },
    validateDDIForm() {
      if (!this.ddiForm.number) return 'DDI number is required.';
      if (!this.isValidDDINumber(this.ddiForm.number)) return 'DDI number must use E.164 format.';
      if (!this.ddiForm.supplier) return 'Supplier is required.';
      return null;
    },
    openDDIDetails(ddi) {
      this.selectedDDIId = ddi.id;
      this.showModal('details');
    },
    openDDIStatusModal(ddi) {
      this.pendingStatusDDI = ddi;
      this.pendingDDIAction = ddi && ddi.status === 'inactive' ? 'enable' : 'disable';
      this.showModal('confirm');
    },
    submitNewDDI() {
      const error = this.validateDDIForm();
      if (error) {
        this.addDDIError = error;
        return;
      }

      this.isSubmittingDDI = true;
      this.addDDIError = null;
      ApiService.createDDI({
        name: this.ddiForm.number,
        description: this.ddiForm.supplier,
        status: this.ddiForm.status,
      })
        .then(() => this.fetchDDIs())
        .then(() => {
          this.hideModal('add');
        })
        .catch((apiError) => {
          this.addDDIError = this.extractApiError(apiError, 'Unable to create DDI.');
        })
        .finally(() => {
          this.isSubmittingDDI = false;
        });
    },
    confirmDDIStatusAction() {
      if (!this.pendingStatusDDI) return;
      const pendingDDIId = this.pendingStatusDDI.id;
      this.isUpdatingDDIStatus = true;
      this.errorMessage = null;
      const actionReq = this.pendingDDIAction === 'enable'
        ? ApiService.enableDDI(pendingDDIId)
        : ApiService.disableDDI(pendingDDIId);

      actionReq
        .then(() => this.fetchDDIs())
        .then(() => {
          this.hideModal('confirm');
          if (this.showModalDetails && this.selectedDDIId === pendingDDIId) {
            const updated = this.normalizedDDIs.find((ddi) => ddi.id === this.selectedDDIId);
            if (updated) {
              this.selectedDDIId = updated.id;
            }
          }
        })
        .catch((error) => {
          const fallback = this.pendingDDIAction === 'enable' ? 'Unable to enable DDI.' : 'Unable to disable DDI.';
          this.errorMessage = this.extractApiError(error, fallback);
        })
        .finally(() => {
          this.isUpdatingDDIStatus = false;
        });
    },
  },
};
</script>

<style scoped>
.mb-g {
  margin-bottom: 1rem;
}
</style>
